import Logger from "../../../clients/sentry";
import { ValidationError, serializeErrorForWarning } from "../../../errors";
import { GridStyleOptions, applyGridStyle, ensureStylesExist } from "../../styles/worksheet";
import { clearRange } from "../../workbook";
import { cacheableData } from "../../workbook/cache";
import { ColumnNameToIndexMap, getReportStartRange, getReportStartRangeFallbackNames } from "./common";
import { rowNames } from "./rangeNames";

export type CellStyle =
  | "header"
  | "sovHeader"
  | "subtotal"
  | "spacer"
  | "spacerOuterBorders"
  | "spacerOuterBorderLeft"
  | "spacerOuterBorderRight"
  | "spacerOuterBorderTop"
  | "item"
  | "gridItem"
  | "markup"
  | "sectionHeader"
  | "assemblyItem"
  | "unitSpecsHeader"
  | "unitTotalsHeader";

export interface RowFormat {
  style: CellStyle;
  group: number[];
  level: number;
  indentColumn?: number;
  cellOverrides?: CellOverride[];
  rowGridStyle?: Omit<GridStyleOptions, "range">;
  adjacentCellStyle?: {
    direction: "left" | "right";
    style: CellStyle;
  };
}

export interface CellOverride {
  columnIndex: number; // column index within the row,
  style?: CellStyle;
  width?: number;
  horizontalAlignment?: Excel.HorizontalAlignment | "Left" | "Center" | "Right";
}

interface printFormattedDataArgs {
  dataArray: (string | number | null)[][];
  formatList?: RowFormat[];
  worksheet: Excel.Worksheet;
  insertAtRangeName: string;
  startColumnIndex: number;
  options?: cacheableData;
  /**
   * The column map the report printed with. Its columns belong to the report, so the template row
   * keeps formulas only outside them. Omit to keep every formula in the template row.
   */
  columnMap?: ColumnNameToIndexMap;
}

/** Offsets of the columns a report writes; `\save_` columns hold user content and are left out. */
const reportOwnedColumnOffsets = (columnMap: ColumnNameToIndexMap): Set<number> =>
  new Set(
    Object.entries(columnMap)
      .filter(([name]) => name !== "startColumnIndex" && !name.startsWith("save_"))
      .map(([, offset]) => offset)
  );

export const printFormattedData = async ({
  dataArray,
  formatList,
  worksheet,
  insertAtRangeName,
  startColumnIndex,
  options,
  columnMap,
}: printFormattedDataArgs) => {
  const resetFirstRowRangeNames = [rowNames.floorAreaItems];
  const startRange = await getReportStartRange(worksheet, insertAtRangeName);
  if (!startRange) {
    throw new ValidationError("The worksheet needs the named range to create your report", {
      rangeName: insertAtRangeName,
    });
  }

  // If startRange has more than one row, then delete all but the first row
  if (startRange.rowCount > 1) {
    const deleteRange = `${startRange.rowIndex + 2}:${startRange.rowIndex + startRange.rowCount}`;
    worksheet.getRange(deleteRange).getEntireRow().delete(Excel.DeleteShiftDirection.up);
    await worksheet.context.sync();
  }

  if (dataArray.length === 0) {
    // if there is no data, we need to clear the intersection of the startRange and the usedRange
    const usedRange = worksheet.getUsedRange();
    const intersection = startRange.getEntireRow().getIntersectionOrNullObject(usedRange);
    await worksheet.context.sync();
    if (!intersection.isNullObject) {
      await clearRange({ range: intersection, preserveFormulas: true });
      await worksheet.context.sync();
    }
    return;
  }

  // The first row is the template filled down below. Formulas in the user's own columns stay; the
  // report's own are dropped, since the row deletion above has already turned theirs into #REF!.
  const firstRow = worksheet.getRangeByIndexes(startRange.rowIndex, startColumnIndex, 1, startRange.columnCount);
  await clearRange({
    range: firstRow,
    preserveFormulas: true,
    clearFormulasInColumns: columnMap ? reportOwnedColumnOffsets(columnMap) : undefined,
  });
  await worksheet.context.sync();

  // Insert rows for the new report
  const startRowIndex = startRange.rowIndex;
  const insertionRange = `${startRowIndex + 2}:${startRowIndex + dataArray.length}`;

  if (dataArray.length > 1) {
    worksheet.getRange(insertionRange).insert(Excel.InsertShiftDirection.down);
  }

  // Derive range to copy entire first row
  const rowCopy = startRange.getRow(0).getEntireRow().load("address");
  // NOTE: Commented out to determine if this is necessary
  // rowCopy.format.wrapText = true;

  // Clear any existing formatting on the row to be copied
  if (resetFirstRowRangeNames.includes(insertAtRangeName)) {
    rowCopy.format.font.set({
      bold: false,
      size: 12,
    });
  }
  await worksheet.context.sync();

  // Fill down entire first row
  if (dataArray.length > 1) {
    worksheet.getRange(insertionRange).copyFrom(rowCopy.address);
  }

  // Define the target range for the data
  const targetRange = worksheet.getRangeByIndexes(
    startRowIndex,
    startColumnIndex,
    dataArray.length,
    dataArray[0].length
  );
  targetRange.load("address");

  const existingNamedRange = worksheet.names.getItemOrNullObject(insertAtRangeName);
  existingNamedRange.load("isNullObject");
  await worksheet.context.sync();

  if (existingNamedRange.isNullObject) {
    worksheet.names.add(insertAtRangeName, targetRange);
  } else {
    existingNamedRange.formula = `=${targetRange.address}`;
  }

  // Drop legacy fallback names so future reads resolve to the canonical name only.
  for (const rangeName of getReportStartRangeFallbackNames(insertAtRangeName)) {
    if (rangeName === insertAtRangeName) continue;
    const legacy = worksheet.names.getItemOrNullObject(rangeName);
    await worksheet.context.sync();
    if (!legacy.isNullObject) {
      legacy.delete();
    }
  }
  await worksheet.context.sync();

  // Load in the data - Excel will parse the static values as data and evaluate the formulas (fix for web Excel not evaluating formulas when set as values)
  targetRange.formulasR1C1 = dataArray;

  // NOTE: Commented out to determine if this is necessary
  // targetRange.format.autofitRows();
  // await worksheet.context.sync();

  // // Enforce minimum row height after autofit (batch get/set via getRowProperties/setRowProperties)
  // const minRowHeight = 25;
  // const rowPropsResult = targetRange.getRowProperties({ format: { rowHeight: true } });
  // await worksheet.context.sync();

  // const rowProps = rowPropsResult.value;
  // const rowHeightsToSet = rowProps.map((row) => ({
  //   format: {
  //     rowHeight: Math.max(row.format?.rowHeight ?? 0, minRowHeight),
  //   },
  // }));
  // targetRange.setRowProperties(rowHeightsToSet);
  await worksheet.context.sync();

  // Apply formatting if formatList is provided
  worksheet.context.application.suspendScreenUpdatingUntilNextSync();
  if (formatList && formatList.length > 0) {
    try {
      await ensureStylesExist();
      let range = targetRange;
      let maxRows = Math.max(formatList.length, dataArray.length);

      if (formatList.length > dataArray.length) {
        const formatListDifference = formatList.length - dataArray.length;
        const rowBelowRange = targetRange.getRowsBelow(formatListDifference);
        range = targetRange.getBoundingRect(rowBelowRange);
      }

      for (let i = 0; i < maxRows; i++) {
        const rowRange = range.getRow(i);
        const formatTag = formatList[i];
        if (formatTag.indentColumn !== undefined) {
          rowRange.getCell(0, formatTag.indentColumn).format.indentLevel = formatTag.level * 2;
        }
        rowRange.style = styleName(formatTag);

        if (formatTag.rowGridStyle) {
          applyGridStyle({
            range: rowRange,
            ...formatTag.rowGridStyle,
          });
        }

        // Apply any cell overrides
        if (formatTag.cellOverrides) {
          for (const override of formatTag.cellOverrides) {
            const cell = rowRange.getCell(0, override.columnIndex);
            if (override.style) {
              cell.style = styleName({ style: override.style, group: [], level: 0 });
            }

            if (override.width) {
              cell.format.columnWidth = override.width;
            }

            if (override.horizontalAlignment) {
              cell.format.horizontalAlignment = override.horizontalAlignment;
            }
          }
        }

        if (formatTag.adjacentCellStyle) {
          const adjacentCol =
            formatTag.adjacentCellStyle.direction === "right"
              ? rowRange.getColumnsAfter(1)
              : rowRange.getColumnsBefore(1);
          adjacentCol.style = styleName({
            style: formatTag.adjacentCellStyle.style,
            group: [],
            level: 0,
          });
        }
      }
      await worksheet.context.sync();
    } catch (error) {
      Logger.warn(
        "Error applying styles to report",
        serializeErrorForWarning(error, {
          worksheetName: worksheet.name,
        })
      );
      //???: What do we do here?
      // throw handleError(error, { worksheetName: worksheet.name });
    }
  }

  // Group rows if required
  if (options?.groupRows && formatList) {
    try {
      await groupRowsInExcel(formatList, targetRange);
    } catch (error) {
      Logger.warn("Error grouping rows in Excel", serializeErrorForWarning(error));
    }
  }
};

export const createColumnNameToIndexMap = async (worksheet: Excel.Worksheet): Promise<ColumnNameToIndexMap> => {
  /* columnMap returns an object that maps column names to their respective column indices
   * The startColumnIndex property is the column index of the first column in the report
   * Any range name that is prefixed with '\' is considered a column that is intended to
   * match a property of objects that will be printed on the report
   * Any range name that is prefixed with '\save_' is considered a preserved column (e.g. \save_notes, \save_comments).
   * Preserve columns (e.g. notes) are filled by reading existing sheet values keyed by code/name/id.
   */

  const columnMap: ColumnNameToIndexMap = { startColumnIndex: 0 };

  await worksheet.context.sync(); // Synchronize the passed worksheet's context
  const namedItems = worksheet.names.load("items");
  await worksheet.context.sync();

  let minColumnIndex = Number.MAX_SAFE_INTEGER;

  // First pass: Determine the minimum column index
  for (const namedItem of namedItems.items) {
    const name = namedItem.name;

    if (name.startsWith("\\")) {
      try {
        const thisRange = namedItem.getRange();
        thisRange.load("columnIndex");
        await worksheet.context.sync();

        if (thisRange.columnIndex < minColumnIndex) {
          minColumnIndex = thisRange.columnIndex;
        }
      } catch (error) {
        console.warn(`Error loading column index for named item "${name}": ${error}`);
      }
    }
  }

  columnMap.startColumnIndex = minColumnIndex;

  // Second pass: Calculate offsets relative to the minimum column index
  for (const namedItem of namedItems.items) {
    const name = namedItem.name;

    if (name.startsWith("\\")) {
      try {
        const thisRange = namedItem.getRange();
        thisRange.load("columnIndex");
        await worksheet.context.sync();
        const columnOffset = thisRange.columnIndex - minColumnIndex;
        // remove the leading '\' from the name and decode any unicode hex encoded characters
        // the expected hex code convention is \x{unicode}\ (e.g., \x1F600\ becomes 😀)
        // this is a workaround for the fact that Excel does not allow certain characters in named ranges

        const decodedName = name
          .slice(1)
          .replace(/\\x([0-9a-fA-F]{1,6})\\/g, (_, hex) => String.fromCodePoint(parseInt(hex, 16)));
        columnMap[decodedName] = columnOffset;
      } catch (error) {
        // pass
      }
    }
  }
  return columnMap;
};

export const handleDynamicColumns = async (
  worksheet: Excel.Worksheet,
  namedRange: string,
  dynamicColumnObjects: any[],
  options?: {
    wrapText?: boolean;
    copyFormat?: boolean;
    minColLength?: number;
  }
) => {
  let dynamicGroupsHeaderRange = worksheet.getRange(`\\${namedRange}`);
  dynamicGroupsHeaderRange.load("rowIndex, columnIndex, columnCount, address");
  await worksheet.context.sync();

  const minColLength = options?.minColLength || 1;
  let colLength = dynamicColumnObjects.length;
  if (colLength < minColLength) {
    colLength = minColLength;
  }
  const colDiff = colLength - dynamicGroupsHeaderRange.columnCount;
  const headerValues = [
    ...(dynamicColumnObjects.length === 0 ? [""] : dynamicColumnObjects.map((colObj) => colObj.name)),
    ...Array(Math.max(0, minColLength - (dynamicColumnObjects.length || 1))).fill(""),
  ];
  // If dynamic groups named range is the right size, update header values
  if (colDiff === 0) {
    dynamicGroupsHeaderRange.values = [headerValues];
    if (options?.wrapText) {
      dynamicGroupsHeaderRange.format.wrapText = true;
    }
    return;
  }
  // Resize columns to match colLength (same pattern as useGroups.ts)
  const dynamicGroupsColumnRange = dynamicGroupsHeaderRange.getEntireColumn();
  dynamicGroupsColumnRange.ungroup("ByColumns");

  // Use usedRange to limit row count instead of entire column (which is 1M+ rows)
  const usedRange = worksheet.getUsedRange();
  usedRange.load("rowIndex, rowCount");
  await worksheet.context.sync();

  const existingColumns = dynamicGroupsHeaderRange.columnCount;
  const startCell = dynamicGroupsHeaderRange.getCell(0, 0);
  const headerRowIndex = dynamicGroupsHeaderRange.rowIndex;
  const lastRowOfUsedRange = usedRange.rowIndex + usedRange.rowCount - 1;
  const rowCountFromHeader = lastRowOfUsedRange - headerRowIndex + 1;
  startCell.format.load("columnWidth");
  startCell.load("columnIndex");
  await worksheet.context.sync();

  if (existingColumns > 1) {
    const toDelete = startCell
      .getOffsetRange(0, 1)
      .getResizedRange(0, existingColumns - 2)
      .getEntireColumn();
    toDelete.delete(Excel.DeleteShiftDirection.left);
    await worksheet.context.sync();
  }

  if (colLength > 1) {
    const newColsEntire = startCell
      .getOffsetRange(0, 1)
      .getResizedRange(0, colLength - 2)
      .getEntireColumn();
    newColsEntire.insert(Excel.InsertShiftDirection.right);
    await worksheet.context.sync();

    // Copy only from header row down (not full column), so content above header is not duplicated
    const copySource = worksheet.getRangeByIndexes(headerRowIndex, startCell.columnIndex, rowCountFromHeader, 1);
    const copyDest = worksheet.getRangeByIndexes(
      headerRowIndex,
      startCell.columnIndex + 1,
      rowCountFromHeader,
      colLength - 1
    );
    copyDest.copyFrom(copySource, options?.copyFormat ? Excel.RangeCopyType.all : Excel.RangeCopyType.formulas);
    newColsEntire.format.columnWidth = startCell.format.columnWidth;
  }
  await worksheet.context.sync();

  const updatedRange = startCell.getResizedRange(0, colLength - 1);

  // Replace the named range with the resized range (same pattern as useGroups.ts)
  worksheet.names.getItemOrNullObject(`\\${namedRange}`).delete();
  await worksheet.context.sync();
  worksheet.names.add(`\\${namedRange}`, updatedRange);
  await worksheet.context.sync();
  dynamicGroupsHeaderRange = worksheet.getRange(`\\${namedRange}`);
  // Update header values
  dynamicGroupsHeaderRange.values = [headerValues];

  if (options?.wrapText) {
    dynamicGroupsHeaderRange.format.wrapText = true;
  }
  if (dynamicColumnObjects.length > 1) {
    dynamicGroupsHeaderRange.getEntireColumn().group("ByColumns");
  }

  return;
};

const styleName = (format: RowFormat): string => {
  switch (format.style) {
    case "header":
      return `Header${Math.min(format.level + 1, 4)}`;
    case "sovHeader":
      return `SOVHeader${Math.min(format.level + 1, 4)}`;
    case "subtotal":
      return `Subtotal${Math.min(format.level + 1, 4)}`;
    case "spacer":
      return "Spacer";
    case "spacerOuterBorders":
      return "SpacerOuterBorders";
    case "spacerOuterBorderLeft":
      return "SpacerOuterBorderLeft";
    case "spacerOuterBorderRight":
      return "SpacerOuterBorderRight";
    case "spacerOuterBorderTop":
      return "SpacerOuterBorderTop";
    case "markup":
      return "Markup";
    case "sectionHeader":
      return "SectionHeader";
    case "assemblyItem":
      return "AssemblyItem";
    case "unitSpecsHeader":
      return "UnitSpecsHeader";
    case "unitTotalsHeader":
      return "UnitTotalsHeader";
    case "gridItem":
      return "GridItem";
    default:
      return "Item";
  }
};

const groupRowsInExcel = async (formatList: RowFormat[], targetRange: Excel.Range): Promise<void> => {
  // Extract unique group levels in descending order, ignoring empty groups
  const uniqueGroups: number[] = Array.from(
    new Set(
      formatList
        .flatMap((format) => format.group) // Collect all group numbers
        .filter((group) => group > 0) // Exclude non-positive group numbers
    )
  ).sort((a, b) => b - a); // Sort descending

  targetRange.load(["worksheet", "rowIndex", "columnIndex", "rowCount", "columnCount"]);
  await targetRange.context.sync();

  const worksheet = targetRange.worksheet;

  //targetRange.context.application.suspendScreenUpdatingUntilNextSync();
  //   this works, but causes the grouping bar to cover the first columns of the sheet
  //   clicking off the sheet and back again fixes it, but the UX still isn't great

  // Iterate through each group level
  for (const groupLevel of uniqueGroups) {
    let start: number | null = null;

    for (let rowIndex = 0; rowIndex < formatList.length; rowIndex++) {
      const format = formatList[rowIndex];
      const isInGroup = format.group.includes(groupLevel);

      if (isInGroup) {
        if (start === null) {
          // Start a new group
          start = rowIndex;
        }
      } else {
        if (start !== null) {
          // End the current group
          groupRowsInExcelRange(worksheet, targetRange, start, rowIndex - 1);
          start = null;
        }
      }
    }

    // Handle the last group for this group level
    if (start !== null) {
      groupRowsInExcelRange(worksheet, targetRange, start, formatList.length - 1);
    }
  }

  await targetRange.context.sync();
};

const groupRowsInExcelRange = async (
  worksheet: Excel.Worksheet,
  range: Excel.Range,
  start: number,
  end: number
): Promise<void> => {
  const groupRange = worksheet.getRangeByIndexes(
    range.rowIndex + start,
    range.columnIndex,
    end - start + 1,
    range.columnCount
  );

  groupRange.getEntireRow().group("ByRows");
};
